import { CalculationService } from './calculation.service';
import { applyLinkedInheritance, buildEqualitySnapshot } from './validators/calc-diff.helper';
import {
  ROLE9_SUBCLASSIFICATION_LINK_ROLE,
  ROLE12_PROFIT_OR_LOSS_LINK_ROLE,
  ROLE13_ANALYSIS_LINK_ROLE,
} from './validators/link-roles';
import { SHEET_KEYS } from '../../models/sheet-keys';

/**
 * Column-visibility context for the SOCIE (610000) recompute.
 *
 * `calculateTotals_610000` short-circuits invisible columns at MOUNT time using
 * the resolved filing-prerequisites visibility (consolidation × period). A
 * mounted caller knows the real visibility (via CrossReferenceService) and
 * passes it here so the recompute matches exactly what the SOCIE editor itself
 * computed — recomputing the SAME visible columns, not a wider all-visible set.
 *
 * `equityColumns` lets a caller override the schema-derived equity columns; when
 * omitted the util falls back to the referenced SOCIE schema's own
 * `equityColumns` (the normal case).
 */
export interface SocieColumnContext {
  visibleColumns: ReadonlyArray<{
    consolidationType: 'Consolidated' | 'Separate';
    periodType: 'Current' | 'Previous';
    visible: boolean;
  }>;
  equityColumns?: Array<{ qname: string }>;
}

/**
 * Cross-sheet "recompute parity" util.
 *
 * WHY THIS EXISTS — the parity bug it closes:
 *   The mounted editor recomputes only the CURRENT sheet's calculated totals
 *   (it calls `calculateTotals()` and patches the result into the form) before
 *   it validates. When that sheet then cross-checks one of its values against
 *   ANOTHER sheet's value (an `equality` rule pointing at a different sheet),
 *   it reads the OTHER sheet's value straight from the persisted snapshot — the
 *   un-recomputed, possibly-stale raw N8N extraction. If that extraction does
 *   not reconcile with the other sheet's line-item sum, the editor surfaces a
 *   spurious "Mismatch" even though BOTH sheets show equal values once the
 *   referenced sheet is opened (which recomputes it). Green tick on the
 *   listing, red cross once you open it — the same drift the headless pure
 *   validators already solved.
 *
 *   The headless validators fixed this for the CURRENT sheet via the pattern
 *   `applyLinkedInheritance → calculateTotals_* → buildEqualitySnapshot` (see
 *   `calc-diff.helper.ts`, `role8-statement-of-financial-position.validator.ts`,
 *   `role12-statement-of-profit-or-loss.validator.ts`). This util mirrors that
 *   same recipe but applies it to the REFERENCED sheets, so a caller can
 *   recompute every other sheet it compares against BEFORE the comparison —
 *   making the mounted cross-sheet read agree with what the user would see.
 *
 * This file is plumbing only — it never decides WHICH rules to run; it just
 * recomputes the totals of the sheets named by a schema's equality rules.
 */

/**
 * Module-level calculator instance — same rationale as `calc-diff.helper.ts`'s
 * `calculator`: `CalculationService` is `@Injectable` for component ergonomics
 * but is a stateless bag of pure methods (empty constructor, no deps), so a
 * single shared instance keeps this util free of Angular DI.
 */
const calculator = new CalculationService();

/** Map MFRS/MPERS to the XBRL prefix `CalculationService` expects. */
function taxonomyToXbrlPrefix(taxonomy: 'MFRS' | 'MPERS'): 'ifrs-full' | 'ifrs-smes' {
  return taxonomy === 'MPERS' ? 'ifrs-smes' : 'ifrs-full';
}

/**
 * The four `dualColumn` (consolidation × period) tuples, all marked visible.
 *
 * `calculateTotals_610000` (SOCIE) is the one calculator that needs a third
 * `options` argument carrying `visibleColumns` (it short-circuits invisible
 * columns at MOUNT time using the resolved filing-prerequisites visibility).
 * This util has no filing-prerequisites context, so it recomputes ALL four
 * columns — recomputing a column the editor would have hidden is harmless: a
 * hidden column is not read by any equality comparison, so its recomputed total
 * is never consumed. Recomputing too few, by contrast, would leave a stale
 * value in a visible column. Erring wide is the safe default here.
 */
const ALL_COLUMNS_VISIBLE: ReadonlyArray<{
  consolidationType: 'Consolidated' | 'Separate';
  periodType: 'Current' | 'Previous';
  visible: boolean;
}> = [
  { consolidationType: 'Consolidated', periodType: 'Current', visible: true },
  { consolidationType: 'Consolidated', periodType: 'Previous', visible: true },
  { consolidationType: 'Separate', periodType: 'Current', visible: true },
  { consolidationType: 'Separate', periodType: 'Previous', visible: true },
];

/**
 * Build the real SOCIE column-visibility context a mounted caller must pass so
 * the recompute matches what the SOCIE editor itself computed.
 *
 * This mirrors `role19-610000-statement-of-changes-in-equity.component.ts`'s
 * own `calculateTotals()` exactly: it builds ONLY the visible (consolidation ×
 * period) tuples and marks each `visible: true` (the editor never feeds an
 * invisible tuple to `calculateTotals_610000` in equity-columns mode):
 *   - Consolidated columns show only when Nature === "Consolidated"
 *     (`crossRefService.isConsolidated()`); Separate always shows.
 *   - Previous-year columns show unless this is a first-time preparation
 *     (`!crossRefService.is1stTimePreparation()` — i.e. subsequent OR unknown
 *     both yield previous-year, matching the editor's default-true branch).
 *
 * Pass `equityColumns` from the referenced SOCIE schema (the cached form JSON's
 * `equityColumns`) so the calc runs in equity-columns mode.
 */
export function buildSocieColumnContext(
  isConsolidated: boolean,
  is1stTimePreparation: boolean,
  equityColumns: Array<{ qname: string }> | undefined
): SocieColumnContext {
  const consolidationTypes: Array<'Consolidated' | 'Separate'> = isConsolidated
    ? ['Consolidated', 'Separate']
    : ['Separate'];
  const periodTypes: Array<'Current' | 'Previous'> = is1stTimePreparation
    ? ['Current']
    : ['Current', 'Previous'];

  const visibleColumns = consolidationTypes.flatMap((consolidationType) =>
    periodTypes.map((periodType) => ({
      consolidationType,
      periodType,
      visible: true,
    }))
  );

  return { visibleColumns, equityColumns };
}

/**
 * Build the SOCIE column-visibility context from an already-resolved
 * dimension-visibility array (the `{ dimension, period, visible }` shape the
 * `dimension-visibility.util` resolver returns).
 *
 * This is the headless counterpart to {@link buildSocieColumnContext}: a mounted
 * caller knows `isConsolidated` / `is1stTimePreparation` (via
 * CrossReferenceService) and builds the context from those booleans; the pure
 * validators have already resolved the four (dimension × period) cells through
 * the shared `resolveDimensionVisibility` registry, so they translate that
 * resolved array here instead of re-deriving it from DEI. Both feed the SAME
 * `recomputeReferencedSheets` path — there is no second copy of the recompute
 * recipe — they differ only in how each derives the visible columns from the
 * inputs it has.
 *
 * Mirrors {@link buildSocieColumnContext}'s output exactly: only the visible
 * tuples are emitted, each marked `visible: true`, with the period token mapped
 * from `CurrentYear`/`PreviousYear` to `Current`/`Previous`.
 */
export function buildSocieColumnContextFromDimensionVisibility(
  visibleColumns: ReadonlyArray<{
    dimension: 'Consolidated' | 'Separate';
    period: 'CurrentYear' | 'PreviousYear';
    visible: boolean;
  }>,
  equityColumns: Array<{ qname: string }> | undefined
): SocieColumnContext {
  return {
    visibleColumns: visibleColumns
      .filter((column) => column.visible)
      .map((column) => ({
        consolidationType: column.dimension,
        periodType: column.period === 'CurrentYear' ? 'Current' : 'Previous',
        visible: true,
      })),
    equityColumns,
  };
}

/**
 * Per-sheet recompute closures, keyed by `SHEET_KEYS`. Each arrow calls the
 * matching `CalculationService.calculateTotals_*` on the module-level
 * `calculator`, mirroring `calc-diff.helper.ts`'s module-level instance.
 *
 * The arrow receives the (already-inherited) leaves, the XBRL prefix, and the
 * referenced sheet's schema — only SOCIE (`610000`) reads the schema, to pull
 * its `equityColumns`. The other four ignore it.
 *
 * Only the five Statement-style sheets that carry calculated totals are
 * registered; every other sheet key (notes, prerequisites, …) is absent, so it
 * passes through `recomputeReferencedSheets` untouched.
 */
type RecomputeFn = (
  leaves: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
  schema: { controls?: any[]; equityColumns?: Array<{ qname: string }> } | undefined,
  socieColumnContext: SocieColumnContext | undefined
) => Record<string, any>;

const PREFIX_CALC_BY_SHEET: Readonly<Record<string, RecomputeFn>> = {
  [SHEET_KEYS.statementOfFinancialPosition]: (leaves, prefix) =>
    calculator.calculateTotals_210000(leaves, prefix),
  [SHEET_KEYS.statementOfProfitOrLoss]: (leaves, prefix) =>
    calculator.calculateTotals_310000(leaves, prefix),
  [SHEET_KEYS.statementOfComprehensiveIncome]: (leaves, prefix) =>
    calculator.calculateTotals_410000(leaves, prefix),
  [SHEET_KEYS.statementOfCashFlowsIndirectMethod]: (leaves, prefix) =>
    calculator.calculateTotals_520000(leaves, prefix),
  [SHEET_KEYS.statementOfChangesInEquity]: (leaves, prefix, schema, socieColumnContext) => {
    // Prefer the caller-supplied real visibility/equityColumns (matches what the
    // SOCIE editor computed). Fall back to the schema's equityColumns + the
    // all-visible default ONLY when no context is supplied (e.g. focused unit
    // tests). See SocieColumnContext / ALL_COLUMNS_VISIBLE.
    const equityColumns = socieColumnContext?.equityColumns ?? schema?.equityColumns;
    const visibleColumns = socieColumnContext?.visibleColumns ?? ALL_COLUMNS_VISIBLE;
    return calculator.calculateTotals_610000(leaves, prefix, {
      hasEquityColumns: Array.isArray(equityColumns) && (equityColumns?.length ?? 0) > 0,
      equityColumns,
      visibleColumns,
    });
  },
};

/**
 * Sheets that inherit leaf line items from a sub-sheet at MOUNT time — so the
 * inherited leaves must be overlaid (via `applyLinkedInheritance`) BEFORE
 * recomputing. Mirrors the editor's `pullRole9Values()` / `pullRole13Values()`.
 *
 * `linkRole` values are single-sourced from `./validators/link-roles.ts`
 * (`ROLE9_SUBCLASSIFICATION_LINK_ROLE = '210100'`,
 * `ROLE13_ANALYSIS_LINK_ROLE = '310100'`,
 * `ROLE12_PROFIT_OR_LOSS_LINK_ROLE = '310000'`) — the same constants the
 * headless validators (`role8-*.validator.ts`, `role12-*.validator.ts`,
 * `role15-*.validator.ts`) import — so the editor's `ui.linkTo.role` codes can
 * never drift across the callers.
 *
 * NOTE the SOCI entry makes this map CHAINED: its source (Role 12) is itself a
 * key here, because Role 12 inherits Role-13 leaves. `resolveInheritedLeaves`
 * therefore recurses into the source before recomputing it.
 */
const INHERITANCE_BY_SHEET: Readonly<
  Record<
    string,
    {
      linkRole: string;
      sourceSheet: string;
      // Recompute the SOURCE sub-sheet's own totals before inheriting them. The
      // editor never inherits the sub-sheet's RAW slice: filing-form-container
      // recomputes Role-9 / Role-13 at load and pull*Values() pulls the
      // RECOMPUTED totals. When the sub-sheet has an internal extracted-vs-sum
      // mismatch, only the recomputed total shifts the host's operands — so
      // inheriting the raw slice here would reintroduce the same false-negative
      // the inline Role-8/Role-12 fixes close. Mirror that recompute here.
      sourceCalc: (
        leaves: Record<string, any>,
        xbrlPrefix: 'ifrs-full' | 'ifrs-smes'
      ) => Record<string, any>;
    }
  >
> = {
  [SHEET_KEYS.statementOfFinancialPosition]: {
    linkRole: ROLE9_SUBCLASSIFICATION_LINK_ROLE, // Role-9 sub-classification
    sourceSheet: SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
    sourceCalc: (leaves, prefix) =>
      calculator.calculateTotals_210100(leaves, prefix),
  },
  [SHEET_KEYS.statementOfProfitOrLoss]: {
    linkRole: ROLE13_ANALYSIS_LINK_ROLE, // Role-13 analysis of profit or loss
    sourceSheet: SHEET_KEYS.analysisOfProfitOrLoss,
    sourceCalc: (leaves, prefix) =>
      calculator.calculateTotals_310100(leaves, prefix),
  },
  [SHEET_KEYS.statementOfComprehensiveIncome]: {
    linkRole: ROLE12_PROFIT_OR_LOSS_LINK_ROLE, // Role-12 statement of profit or loss
    sourceSheet: SHEET_KEYS.statementOfProfitOrLoss,
    sourceCalc: (leaves, prefix) =>
      calculator.calculateTotals_310000(leaves, prefix),
  },
};

/**
 * The leaves a sheet should be recomputed from: its own slice with any
 * `ui.linkTo`-inherited cells overlaid.
 *
 * RECURSIVE, because inheritance chains: SOCI <- SOPL <- Analysis of P&L. The
 * source's own inherited leaves are resolved before its totals are recomputed,
 * so SOCI reads the same `Profit (loss)` the editor shows rather than one
 * computed from Role 12's stale persisted leaves. Termination is structural —
 * Role 9 and Role 13 carry no INHERITANCE_BY_SHEET entry, so the chain is at
 * most two deep today and cannot cycle unless someone declares one.
 *
 * Degrades gracefully at every step: no registry entry, no schema controls, or
 * an empty source slice all return the slice untouched.
 */
function resolveInheritedLeaves(
  sheetKey: string,
  slice: Record<string, any>,
  allSheetData: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
  schemaFor: (sheetKey: string) => { controls?: any[] } | undefined
): Record<string, any> {
  const inheritance = INHERITANCE_BY_SHEET[sheetKey];
  const schema = schemaFor(sheetKey);
  if (!inheritance || !Array.isArray(schema?.controls)) return slice;

  const rawSource = allSheetData[inheritance.sourceSheet];
  if (!rawSource || Object.keys(rawSource).length === 0) return slice;

  const sourceLeaves = resolveInheritedLeaves(
    inheritance.sourceSheet,
    rawSource,
    allSheetData,
    xbrlPrefix,
    schemaFor
  );
  const recomputedSource = buildEqualitySnapshot(
    sourceLeaves,
    inheritance.sourceCalc(sourceLeaves, xbrlPrefix)
  );

  return applyLinkedInheritance(
    schema!.controls!,
    slice,
    recomputedSource,
    inheritance.linkRole
  );
}

/**
 * Collect the distinct `rule.sheet` values from every
 * `control.validation.equality[]` in `schema`, EXCLUDING `currentSheetKey`.
 *
 * These are the OTHER sheets this schema compares its values against — i.e. the
 * exact set `recomputeReferencedSheets` must recompute so the comparison reads
 * fresh (not stale-extracted) operands.
 */
export function referencedSheetKeysFromSchema(
  schema: any,
  currentSheetKey: string
): string[] {
  const referenced = new Set<string>();

  const controls: any[] = Array.isArray(schema?.controls) ? schema.controls : [];
  for (const control of controls) {
    const equalityRules: any[] = Array.isArray(control?.validation?.equality)
      ? control.validation.equality
      : [];
    for (const rule of equalityRules) {
      const sheet = rule?.sheet;
      if (typeof sheet === 'string' && sheet !== currentSheetKey) {
        referenced.add(sheet);
      }
    }
  }

  return Array.from(referenced);
}

/**
 * Return a SHALLOW COPY of `allSheetData` where, for each referenced sheet key
 * that has a registered calculator AND a non-empty slice, the calculated totals
 * are recomputed and overlaid (so a caller compares against fresh operands).
 *
 * For each referenced sheet:
 *   1. If the sheet inherits leaves (SFP←role9, P&L←role13) AND `schemaFor`
 *      returns controls, overlay the inherited leaves first via
 *      `applyLinkedInheritance`. If `schemaFor` returns no controls, inheritance
 *      is skipped and the recompute runs from the raw slice (degrade
 *      gracefully).
 *   2. Recompute totals via the per-sheet `calculateTotals_*` closure.
 *   3. Overlay the recomputed totals onto the leaves via `buildEqualitySnapshot`.
 *
 * A referenced sheet with no registered calculator, or an empty/absent slice,
 * passes through untouched (same object reference). The input `allSheetData`
 * and every slice within it are never mutated.
 */
export function recomputeReferencedSheets(
  allSheetData: Record<string, any>,
  referencedSheetKeys: ReadonlyArray<string>,
  taxonomy: 'MFRS' | 'MPERS',
  schemaFor: (sheetKey: string) => { controls?: any[] } | undefined,
  socieColumnContext?: SocieColumnContext
): Record<string, any> {
  const out: Record<string, any> = { ...allSheetData };
  const xbrlPrefix = taxonomyToXbrlPrefix(taxonomy);

  for (const sheetKey of referencedSheetKeys) {
    const calc = PREFIX_CALC_BY_SHEET[sheetKey];
    if (!calc) continue; // no calculator for this sheet — pass through.

    const slice = allSheetData[sheetKey];
    if (!slice || Object.keys(slice).length === 0) continue; // empty/absent — pass through.

    const schema = schemaFor(sheetKey);

    // GLOBAL RULE (PO, 2026-06-19, REVISED) — referenced operands are ALWAYS
    // recompute-of-latest; the N8N snapshot is NEVER used as a cross-sheet
    // comparison operand.
    //
    // An earlier revision anchored the referenced operand to its N8N baseline
    // (recompute the baseline's own leaves, then let the extracted totals win) so
    // a sub-sheet recompute divergence stayed a non-blocking warning. But that
    // made the SNAPSHOT an operand in a cross-sheet BLOCKING comparison, which the
    // PO rule forbids: the snapshot is only ever compared against the recompute/
    // latest of the SAME field (the "does not match the extracted sum" Suggested
    // Change, owned by the referenced sheet itself). An internally-inconsistent
    // extraction (stored total ≠ its own leaves, e.g. Perm 04) therefore drove a
    // false cross-sheet block. We now always recompute from the CURRENT leaves
    // (with sub-sheet inheritance) below — exactly what the mounted editor reads.

    // Step 1: overlay inherited leaves first (only for inheriting sheets with a
    // schema that exposes controls). The source sub-sheet's totals are
    // recomputed BEFORE inheriting — and recursively, since a source may itself
    // inherit — so the host reads the same operands the editor does. Every
    // degenerate case (no entry, no controls, empty source) returns the slice
    // untouched. See `resolveInheritedLeaves`.
    const leaves = resolveInheritedLeaves(
      sheetKey,
      slice,
      allSheetData,
      xbrlPrefix,
      schemaFor
    );

    // Step 2: recompute totals from the (possibly inherited) leaves. Only the
    // SOCIE closure reads socieColumnContext; the other four ignore it.
    const recomputed = calc(
      leaves,
      xbrlPrefix,
      schema as { controls?: any[]; equityColumns?: Array<{ qname: string }> } | undefined,
      socieColumnContext
    );

    // Step 3: overlay the recomputed totals onto the leaves.
    out[sheetKey] = buildEqualitySnapshot(leaves, recomputed);
  }

  return out;
}
